import React from 'react';

import { Menu } from '@base-ui/react/menu';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { themeV5 } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { DeclarativeMenuItem } from './DeclarativeMenuItem';
import { DeclarativeMenuPopup } from './DeclarativeMenuPopup';
import { DeclarativeMenuSeparator } from './DeclarativeMenuSeparator';

export type DeclarativeMenuConfig = DeclarativeMenuChildConfig[];
export type DeclarativeMenuChildConfig = DeclarativeMenuItemConfig | DeclarativeMenuSeparator;

/**
 * Include this value anywhere menu items can be placed to produce a separator. Separators at the
 * beginning or end of a menu are hidden automatically, and sequences of two or more separators are
 * collapsed into one, so there's generally no need to include these conditionally.
 */
export const DECLARATIVE_MENU_SEPARATOR = Symbol('---');
export type DeclarativeMenuSeparator = typeof DECLARATIVE_MENU_SEPARATOR;

export interface DeclarativeMenuItemConfig {
  label: string;
  /**
   * If present, this menu item will behave as a controlled checkbox. This cannot be used at the
   * same time as the icon option.
   */
  checked?: boolean;
  /**
   * Set this to false to disable the menu item while keeping it visible.
   * @default true
   */
  enabled?: boolean;
  /**
   * Hint text displayed to the right of the menu item. Use this for displaying keyboard shortcut
   * hints.
   */
  hint?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  /**
   * If present, this menu item will act as a trigger for a submenu, the contents of which are
   * specified by the value of this option.
   */
  submenu?: DeclarativeMenuConfig;
  /**
   * Hover text. Still shown when the item is disabled.
   */
  tooltip?: string;
  /**
   * Set this to false to hide the menu item entirely.
   * @default true
   */
  visible?: boolean;
}

interface BuildDeclarativeMenuOptions {
  /** Position the menu against this instead of its trigger, such as a point for a context menu. */
  anchor?: React.ComponentProps<typeof Menu.Positioner>['anchor'];
  /** @private Used internally to construct submenus. */
  isSubmenu?: boolean;
  zIndex?: number;
}

/**
 * Factory function that constructs the React components for a menu described using a declarative
 * config object.
 */
export function buildDeclarativeMenu(
  itemConfigs: DeclarativeMenuConfig,
  options: BuildDeclarativeMenuOptions = {}
): React.ReactNode {
  const { anchor, isSubmenu = false, zIndex = themeV5.zIndex.modal } = options;

  /**
   * If one or more menu items have icons or are checkable, automatically leave a gap to the left of
   * menu items that do not have icons.
   */
  const hasIconGap = itemConfigs.some(
    config => config !== DECLARATIVE_MENU_SEPARATOR && (config.icon || config.checked !== undefined)
  );

  const content = itemConfigs.map((config, index) => {
    if (config === DECLARATIVE_MENU_SEPARATOR) return <DeclarativeMenuSeparator key={index} />;

    const {
      checked,
      enabled = true,
      hint,
      icon,
      label,
      onClick,
      submenu,
      tooltip,
      visible = true,
    } = config;
    if (!visible) return null;

    // Labels can repeat within a menu, so items are keyed by their fixed place in the config
    const item = (
      <DeclarativeMenuItem
        key={index}
        checked={checked}
        disabled={!enabled}
        hint={hint}
        icon={icon ?? (hasIconGap ? 'gap' : 'none')}
        isSubmenuTrigger={!!submenu}
        onClick={onClick}
      >
        {label}
      </DeclarativeMenuItem>
    );

    if (submenu)
      return (
        <Menu.SubmenuRoot key={index}>
          {item}
          {buildDeclarativeMenu(submenu, { isSubmenu: true, zIndex })}
        </Menu.SubmenuRoot>
      );

    if (tooltip)
      return (
        <MenuItemTooltip key={index} title={tooltip}>
          {item}
        </MenuItemTooltip>
      );

    return item;
  });

  return (
    <Menu.Portal>
      <Menu.Positioner
        align="start"
        alignOffset={isSubmenu ? -4 : 0}
        anchor={anchor}
        positionMethod="fixed"
        side={isSubmenu ? 'right' : 'bottom'}
        sideOffset={4}
        style={{ zIndex }}
      >
        <DeclarativeMenuPopup>{content}</DeclarativeMenuPopup>
      </Menu.Positioner>
    </Menu.Portal>
  );
}

const useTooltipStyles = makeStyles()(() => ({
  target: {
    display: 'block',
  },
}));

function MenuItemTooltip({
  children,
  title,
}: {
  children: React.ReactElement;
  title: string;
}): JSX.Element {
  const { classes } = useTooltipStyles();

  return (
    <AlineTooltip arrow placement="right" title={title}>
      <span className={classes.target}>{children}</span>
    </AlineTooltip>
  );
}
